import { useEffect, useRef, useState } from "react";
import { Link } from "@/components/Link";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Edit,
  Save,
  X,
  Trash2,
  Workflow,
  ExternalLink,
  HelpCircle,
} from "lucide-react";
import { IssuesBadge } from "./FieldFlag";
import type { DraftValidation } from "@/lib/draftValidation";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type Props = {
  storeName: string;
  storeSlugOrId: string;
  isEditMode: boolean;
  hasChanges: boolean;
  isSaving: boolean;
  onEnableEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onOpenScraper: () => void;
  // Queue
  currentIndex: number; // -1 if not in queue
  total: number;
  prevId: string | null;
  nextId: string | null;
  onPrev: () => void;
  onNext: () => void;
  // Validation
  validation: DraftValidation;
  // Bottom-bar actions
  rating: number;
  onRatingChange: (value: number) => void;
  ratingSaving: boolean;
  ratingNeedsAttention: boolean;
  onDelete: () => void;
  onPublish: () => void;
  onPublishAndNext: () => void;
  publishing: boolean;
  deleting: boolean;
  onShowHelp?: () => void;
};

export function ReviewChrome(props: Props) {
  const {
    storeName,
    storeSlugOrId,
    isEditMode,
    hasChanges,
    isSaving,
    onEnableEdit,
    onSaveEdit,
    onCancelEdit,
    onOpenScraper,
    currentIndex,
    total,
    prevId,
    nextId,
    onPrev,
    onNext,
    validation,
    rating,
    onRatingChange,
    ratingSaving,
    ratingNeedsAttention,
    onDelete,
    onPublish,
    onPublishAndNext,
    publishing,
    deleting,
    onShowHelp,
  } = props;

  return (
    <>
      <TopBar
        storeName={storeName}
        currentIndex={currentIndex}
        total={total}
        prevId={prevId}
        nextId={nextId}
        onPrev={onPrev}
        onNext={onNext}
        onOpenScraper={onOpenScraper}
        isEditMode={isEditMode}
        hasChanges={hasChanges}
        isSaving={isSaving}
        onEnableEdit={onEnableEdit}
        onSaveEdit={onSaveEdit}
        onCancelEdit={onCancelEdit}
        validation={validation}
        onShowHelp={onShowHelp}
      />
      <BottomBar
        storeSlugOrId={storeSlugOrId}
        rating={rating}
        onRatingChange={onRatingChange}
        ratingSaving={ratingSaving}
        ratingNeedsAttention={ratingNeedsAttention}
        onDelete={onDelete}
        onPublish={onPublish}
        onPublishAndNext={onPublishAndNext}
        publishing={publishing}
        deleting={deleting}
        nextDisabled={!nextId}
      />
    </>
  );
}

function TopBar({
  storeName,
  currentIndex,
  total,
  prevId,
  nextId,
  onPrev,
  onNext,
  onOpenScraper,
  isEditMode,
  hasChanges,
  isSaving,
  onEnableEdit,
  onSaveEdit,
  onCancelEdit,
  validation,
  onShowHelp,
}: {
  storeName: string;
  currentIndex: number;
  total: number;
  prevId: string | null;
  nextId: string | null;
  onPrev: () => void;
  onNext: () => void;
  onOpenScraper: () => void;
  isEditMode: boolean;
  hasChanges: boolean;
  isSaving: boolean;
  onEnableEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  validation: DraftValidation;
  onShowHelp?: () => void;
}) {
  return (
    <div className="sticky top-0 z-30 bg-white border-b shadow-sm" dir="ltr">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2.5 flex items-center gap-3">
        <Link
          to="/admin?tab=stores"
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 shrink-0"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Admin</span>
        </Link>

        <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800 border border-yellow-300 font-medium shrink-0">
          Draft
        </span>

        <span className="font-semibold text-sm truncate flex-1 min-w-0">
          {storeName || "Untitled draft"}
        </span>

        <IssuesBadge
          required={validation.required}
          recommended={validation.recommended}
          warnings={validation.warnings}
        />

        {total > 0 && (
          <span className="text-xs text-gray-500 whitespace-nowrap shrink-0 hidden sm:inline">
            {currentIndex >= 0 ? currentIndex + 1 : "?"} of {total}
          </span>
        )}

        <div className="flex items-center gap-0.5 shrink-0">
          <KeyHintButton onClick={onPrev} disabled={!prevId} hint="K">
            <ChevronLeft className="w-4 h-4" />
          </KeyHintButton>
          <KeyHintButton onClick={onNext} disabled={!nextId} hint="J">
            <ChevronRight className="w-4 h-4" />
          </KeyHintButton>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={onOpenScraper}
          className="h-8 gap-1.5 shrink-0"
          title="Scraper run (S)"
        >
          <Workflow className="w-3.5 h-3.5" />
          <span className="hidden md:inline">Scraper</span>
          <KeyHint k="S" />
        </Button>

        {isEditMode ? (
          <div className="flex gap-1 shrink-0">
            <Button
              size="sm"
              onClick={onSaveEdit}
              disabled={!hasChanges || isSaving}
              className="h-8 bg-emerald-600 hover:bg-emerald-700"
            >
              <Save className="w-3.5 h-3.5 mr-1" />
              {isSaving ? "..." : "Save"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onCancelEdit}
              disabled={isSaving}
              className="h-8 px-2"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={onEnableEdit}
            className="h-8 gap-1 shrink-0"
            title="Edit (E)"
          >
            <Edit className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Edit</span>
            <KeyHint k="E" />
          </Button>
        )}

        {onShowHelp && (
          <button
            onClick={onShowHelp}
            className="text-gray-400 hover:text-gray-600 shrink-0"
            title="Keyboard shortcuts (?)"
          >
            <HelpCircle className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function BottomBar({
  storeSlugOrId,
  rating,
  onRatingChange,
  ratingSaving,
  ratingNeedsAttention,
  onDelete,
  onPublish,
  onPublishAndNext,
  publishing,
  deleting,
  nextDisabled,
}: {
  storeSlugOrId: string;
  rating: number;
  onRatingChange: (value: number) => void;
  ratingSaving: boolean;
  ratingNeedsAttention: boolean;
  onDelete: () => void;
  onPublish: () => void;
  onPublishAndNext: () => void;
  publishing: boolean;
  deleting: boolean;
  nextDisabled: boolean;
}) {
  return (
    <div className="sticky bottom-0 z-30 bg-white border-t shadow-lg" dir="ltr">
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2.5 flex flex-wrap items-center gap-3 justify-between">
        <Button
          onClick={onDelete}
          variant="destructive"
          size="sm"
          disabled={deleting || publishing}
          className="h-10"
        >
          <Trash2 className="w-4 h-4 mr-1" />
          {deleting ? "..." : "Delete"}
        </Button>

        <RatingControl
          value={rating}
          onChange={onRatingChange}
          saving={ratingSaving}
          needsAttention={ratingNeedsAttention}
        />

        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm" className="h-10">
            <a
              href={`/store/${storeSlugOrId}`}
              target="_blank"
              rel="noopener noreferrer"
              title="Open public preview in new tab"
            >
              <ExternalLink className="w-4 h-4" />
              <span className="hidden md:inline ml-1">Open</span>
            </a>
          </Button>
          <Button
            onClick={onPublish}
            disabled={publishing || deleting}
            className="h-10 bg-emerald-600 hover:bg-emerald-700"
            title="Publish (P)"
          >
            {publishing ? "Publishing..." : "Publish"}
            <KeyHint k="P" tone="dark" />
          </Button>
          <Button
            onClick={onPublishAndNext}
            disabled={publishing || deleting || nextDisabled}
            className="h-10 bg-teal-600 hover:bg-teal-700"
            title="Publish & Next (Shift+P)"
          >
            Publish & Next
            <ChevronRight className="w-4 h-4 ml-1" />
            <KeyHint k="⇧P" tone="dark" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function RatingControl({
  value,
  onChange,
  saving,
  needsAttention,
}: {
  value: number;
  onChange: (v: number) => void;
  saving: boolean;
  needsAttention: boolean;
}) {
  const [local, setLocal] = useState(value);
  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current) {
      setLocal(value);
      initialized.current = true;
      return;
    }
    if (!saving) setLocal(value);
  }, [value, saving]);

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-gray-500">Rating</span>
      <input
        type="range"
        min={0}
        max={10}
        step={0.5}
        value={local}
        onChange={(e) => {
          const v = Number(e.target.value);
          setLocal(v);
          onChange(v);
        }}
        className={`w-32 ${needsAttention ? "accent-amber-500" : ""}`}
        disabled={saving}
      />
      <span
        className={`text-xs font-semibold w-8 text-right ${
          needsAttention ? "text-amber-600 animate-pulse" : "text-gray-700"
        }`}
      >
        {local.toFixed(1)}
      </span>
    </div>
  );
}

function KeyHint({ k, tone = "light" }: { k: string; tone?: "light" | "dark" }) {
  return (
    <kbd
      className={`hidden lg:inline-block ml-1.5 px-1 py-0 rounded text-[9px] font-mono border ${
        tone === "dark"
          ? "bg-white/15 border-white/20 text-white/80"
          : "bg-gray-100 border-gray-200 text-gray-500"
      }`}
    >
      {k}
    </kbd>
  );
}

function KeyHintButton({
  onClick,
  disabled,
  hint,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClick}
            disabled={disabled}
            className="h-8 w-8 p-0"
          >
            {children}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          Press <kbd className="px-1 bg-gray-100 rounded font-mono">{hint}</kbd>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function ShortcutHelpDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-sm w-full p-5 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-semibold text-sm">Keyboard shortcuts</h3>
        <ul className="text-xs space-y-1.5">
          <Row k="J / →" desc="Next draft" />
          <Row k="K / ←" desc="Previous draft" />
          <Row k="E" desc="Toggle edit mode" />
          <Row k="P" desc="Publish" />
          <Row k="Shift + P" desc="Publish & next" />
          <Row k="D" desc="Delete store" />
          <Row k="S" desc="Toggle scraper drawer" />
          <Row k="Esc" desc="Close drawer" />
          <Row k="?" desc="Show this help" />
        </ul>
        <div className="text-right">
          <Button size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ k, desc }: { k: string; desc: string }) {
  return (
    <li className="flex items-center justify-between gap-3">
      <span className="text-gray-600">{desc}</span>
      <kbd className="px-1.5 py-0.5 rounded bg-gray-100 border text-[10px] font-mono">
        {k}
      </kbd>
    </li>
  );
}
